/**
 * Notification dialog component for reusable alert and confirmation flows.
 * @exports NotificationDialog Component (default)
 * @author {Cognizant Technology Solution}
 * @flow
 */
import React from 'react';
import NikeButton from '../../nikeCustomComponents/nikeButton';
import NikeDialog from '../../nikeCustomComponents/nikeDialog';
import Message from '../../constants/messageText';
import './styles.scss';

interface Action {
  buttonText: string;
  onClick: Function;
  testid?: string;
}

interface Props {
  image?: {
    src: Object;
    alt: string;
  };
  title?: string;
  description?: string;
  actions?: Array<Action>;
  open: boolean;
  className?: string;
  onClose?: Function;
  renderCustomContent?: Function;
}

/**
 * Builds the default action set for notification dialogs based on error state.
 * @param {Function} setRequestError Setter used to clear the error state
 * @param {Object} errorCodeText Error payload used to choose the action
 * @returns {Array<Object>} Dialog action list
 */
export const dialogAction = (setRequestError: Function, errorCodeText: Object) => {
  const returnToHome = () => {
    window.location.href = '/';
  };
  if (errorCodeText && errorCodeText.errSessionTimeout) {
    return ([{
      buttonText: Message.Refresh,
      onClick: () => { window.location.href = '/'; }
    }]);
  }
  if (
    errorCodeText
    && errorCodeText?.errTitle
    && errorCodeText?.errTitle === Message.createProfileTitle
  ) {
    return ([{
      buttonText: Message.createProfile,
      onClick: returnToHome
    }]);
  }
  return ([{
    buttonText: Message.Dismiss,
    onClick: () => setRequestError(false)
  }]);
};

/**
 * Renders a reusable notification dialog with optional custom content/actions.
 * @param {Props} props Notification dialog content and behavior
 * @returns {Object} NotificationDialog component
 */
export default function NotificationDialog(props: Props) {
  const {
    image,
    title,
    description,
    actions = [],
    open,
    className,
    onClose,
    renderCustomContent
  } = props;

  const handleClose = (event, reason) => {
    if (onClose && ((reason === 'escapeKeyDown') || (reason === 'backdropClick'))) {
      onClose(event, reason);
    }
  };
  /**
    * This method is used as handler for rendering
    * the notification dialog content.
    */
  const dialogContent = (
    <>
      {
        image && image.src && (
          <div className="imageContainer">
            <img src={image.src} alt={image.alt} />
          </div>
        )
      }
      <div className="textContainer">
        <div className="title">{title}</div>
        <span className="description">
          {description}
        </span>
        {
          renderCustomContent && renderCustomContent()
        }
      </div>
    </>
  );
  /**
     * This method is used as handler for rendering
     * the notification dialog actions like close, download etc.
     */
  const renderActions = (
    <div>
      {
        actions && actions.map((action) => (
          <NikeButton
            key={action.buttonText}
            buttonText={action.buttonText}
            onClick={action.onClick}
            variant="rounded-black"
            testid={action.testid || action.buttonText}
            data-testid={action.testid || action.buttonText}
          />
        ))
      }
    </div>
  );

  return (
    <NikeDialog
      onClose={handleClose}
      open={open}
      className={`notificationDialog ${className || ''}`}
      renderContent={dialogContent}
      renderActions={renderActions}
      data-testid="notification-dialog"
    />
  );
}
